import type { Language } from "../types/preferences";

export const copy = {
  de: {
    appTitle: "Schweißparameter-Rechner",
    ready: "System bereit",
    module: "Modul",
    active: "Aktiv",
    planned: "Geplant",
    preferences: "Einstellungen",
    language: "Sprache",
    units: "Einheiten",
    theme: "Darstellung",
    themes: {
      jarvis: "JARVIS",
      m3Light: "M3 Hell",
      m3Dark: "M3 Dunkel"
    },
    exportSettings: "Export",
    importSettings: "Import",
    calculationTarget: "Berechnungsziel",
    targetSelection: "Berechnungsziel",
    inputParameters: "Eingabeparameter",
    result: "Ergebnis",
    formula: "Formel",
    modules: {
      weld: "a-Maß Rechner",
      weldDescription: "a-Maß, Geschwindigkeit, Drahtvorschub und Draht-Ø.",
      wireConversion: "Drahtdurchmesser Rechner",
      wireConversionDescription: "vD auf einen anderen Drahtdurchmesser umrechnen.",
      geometry: "a/Z Maß Rechner",
      geometryDescription: "a-Maß und Z-Maß über den Kehlnahtwinkel umrechnen.",
      sheetThickness: "Blechdicken Rechner",
      sheetThicknessDescription: "Min- und Max-Empfehlungen zur Blechdicke.",
      wireMaterial: "Drahtverbrauch Rechner",
      wireMaterialDescription: "Spulenreichweite, Schweißzeit und Drahtverbrauch.",
      deposition: "Abschmelzraten Rechner",
      depositionDescription: "Abschmelzrate aus Drahtdaten und Drahtvorschub.",
      timeSpeed: "vs/time Rechner",
      timeSpeedDescription: "Nahtlänge, Dauer und Schweißgeschwindigkeit.",
      energy: "Streckenenergie Rechner",
      energyDescription: "Streckenenergie aus IE oder IP und Schweißgeschwindigkeit.",
      multiLayer: "Mehrlagenschweißung",
      multiLayerDescription: "Lagenaufbau und Raupenplanung.",
      robot: "ABB Pendeln",
      robotDescription: "Pendelparameter für Roboterprogramme."
    },
    wireConversion: {
      sourceDiameter: "Angegebener Draht-Ø",
      sourceShort: "Ausgang",
      sourceWireFeed: "Angegebener vD",
      targetDiameter: "Gewünschter Draht-Ø",
      targetShort: "Ziel",
      targetWireFeed: "Umgerechneter vD",
      sourceArea: "Drahtfläche Ausgang",
      targetArea: "Drahtfläche Ziel",
      areaRatio: "Flächenverhältnis"
    },
    geometry: {
      aMass: "a-Maß",
      zMeasureInput: "Z-Maß",
      angle: "Öffnungswinkel Kehlnaht",
      angleShort: "Winkel",
      targetSelection: "Berechnungsziel",
      calculateZ: "Z-Maß berechnen",
      calculateZDescription: "Aus a-Maß und Winkel das Z-Maß berechnen.",
      calculateA: "a-Maß berechnen",
      calculateADescription: "Aus Z-Maß und Winkel das a-Maß berechnen.",
      zMeasure: "Gerechnetes Z-Maß",
      calculatedAMass: "Gerechnetes a-Maß",
      zMeasureInch: "Z-Maß in inch",
      zMeasureFraction: "Z-Maß als inch-Bruch",
      legFactor: "Z-Faktor",
      halfAngle: "Halber Winkel"
    },
    sheetThickness: {
      aMass: "a-Maß",
      presetSelection: "a-Maß Schnellauswahl",
      matrix: "Blechdicken-Tabelle",
      recommendation: "Empfohlene Blechdicke",
      froniusRecommendation: "Empfehlung Fronius",
      enCalculation: "EN 1993-1-8/NA:2020",
      julesCalculation: "Jules-Faktor (0,83)",
      froniusMin: "Fronius min.",
      froniusMax: "Fronius max.",
      normMin: "Min. nach EN 1993-1-8/NA",
      normMax: "Max. nach EN 1993-1-8/NA",
      julesMin: "Min. mit Jules-Faktor",
      julesFactor: "Jules-Faktor"
    },
    wireMaterial: {
      commonData: "Gemeinsame Drahtdaten",
      spoolRange: "Spule / Reichweite",
      rangeConsumption: "Reichweite & Verbrauch",
      spoolWeight: "Spulengewicht",
      spoolShort: "Spule",
      density: "Werkstoffdichte",
      densityShort: "Dichte",
      weldingTime: "Schweißzeit",
      timeShort: "Zeit",
      material: "Zusatzwerkstoff",
      customMaterial: "Manueller Wert",
      wireLength: "Drahtlänge",
      maxWeldTime: "Max. Schweißzeit",
      maxWeldTimeHours: "Max. Schweißzeit",
      depositionRate: "Abschmelzrate",
      wireConsumption: "Drahtverbrauch",
      weightPerMeter: "Drahtgewicht pro Meter",
      materials: {
        steel: "Stahl",
        stainless: "CrNi",
        almg5: "AlMg5",
        alsi5: "AlSi5",
        inconel625: "Inconel",
        cusi3: "CuSi3"
      }
    },
    timeSpeed: {
      calculateSpeed: "vs berechnen",
      calculateDuration: "Dauer berechnen",
      weldLength: "Schweißnahtlänge",
      lengthShort: "Länge",
      duration: "Dauer",
      durationShort: "Dauer",
      travelSpeedResult: "Berechnete Schweißgeschwindigkeit",
      durationResult: "Berechnete Dauer",
      durationMinutes: "Dauer in Minuten",
      lengthPerSecond: "Nahtlänge pro Sekunde"
    },
    energy: {
      impulseMode: "Aus IE und Länge",
      powerMode: "Aus IP und vs",
      energyComparison: "Streckenenergie Vergleich",
      impulseEnergy: "IE",
      weldLength: "Schweißnahtlänge",
      power: "IP",
      impulseMethod: "Streckenenergie aus IE",
      powerMethod: "Streckenenergie aus IP und vs",
      difference: "Abweichung",
      differencePercent: "Abweichung relativ"
    },
    modes: {
      aMass: {
        label: "a-Maß berechnen",
        shortLabel: "a-Maß",
        description: "Prognostizierte Kehlnahtdicke mit Sicherheitsfaktor."
      },
      travelSpeed: {
        label: "vs berechnen",
        shortLabel: "vs",
        description: "Aus a-Maß, Drahtdurchmesser und Drahtvorschub."
      },
      wireFeed: {
        label: "Drahtvorschub berechnen",
        shortLabel: "vD",
        description: "Aus a-Maß, Drahtdurchmesser und Schweißgeschwindigkeit."
      },
      wireDiameter: {
        label: "Draht-Ø berechnen",
        shortLabel: "Draht-Ø",
        description: "Aus a-Maß, vD und vs."
      }
    },
    inputs: {
      aMass: "a-Maß",
      wireDiameter: "Draht-Ø",
      wireFeed: "Drahtvorschub vD",
      travelSpeed: "Schweißgeschwindigkeit vs"
    },
    labels: {
      predictedAMass: "Prognostiziertes a-Maß",
      rawAMass: "Theoretisches a-Maß ohne Faktor",
      travelSpeed: "Berechnete Schweißgeschwindigkeit",
      wireFeed: "Berechneter Drahtvorschub",
      wireDiameter: "Berechneter Drahtdurchmesser",
      seamArea: "Nahtquerschnitt",
      wireArea: "Drahtfläche",
      safetyFactor: "Sicherheitsfaktor",
      volumeRate: "Volumenstrom"
    }
  },
  en: {
    appTitle: "Welding Parameter Calculator",
    ready: "System ready",
    module: "Module",
    active: "Active",
    planned: "Planned",
    preferences: "Preferences",
    language: "Language",
    units: "Units",
    theme: "Appearance",
    themes: {
      jarvis: "JARVIS",
      m3Light: "M3 Light",
      m3Dark: "M3 Dark"
    },
    exportSettings: "Export",
    importSettings: "Import",
    calculationTarget: "Calculation target",
    targetSelection: "Calculation target",
    inputParameters: "Input parameters",
    result: "Result",
    formula: "Formula",
    modules: {
      weld: "Fillet Throat Calculator",
      weldDescription: "Throat size, travel speed, wire feed and wire diameter.",
      wireConversion: "Wire Diameter Calculator",
      wireConversionDescription: "Convert wire feed speed to another wire diameter.",
      geometry: "Throat / Leg Size Calculator",
      geometryDescription: "Convert throat size and leg size via the fillet weld angle.",
      sheetThickness: "Plate Thickness Calculator",
      sheetThicknessDescription: "Min and max recommendations for plate thickness.",
      wireMaterial: "Wire Consumption Calculator",
      wireMaterialDescription: "Spool range, welding time and wire consumption.",
      deposition: "Deposition Rate Calculator",
      depositionDescription: "Deposition rate from wire data and wire-feed speed.",
      timeSpeed: "Travel Speed / Time Calculator",
      timeSpeedDescription: "Weld length, duration and travel speed.",
      energy: "Heat Input Calculator",
      energyDescription: "Heat input from IE or IP and travel speed.",
      multiLayer: "Multi-layer welding",
      multiLayerDescription: "Layer build-up and bead planning.",
      robot: "ABB weaving",
      robotDescription: "Weaving parameters for robot programs."
    },
    wireConversion: {
      sourceDiameter: "Source wire Ø",
      sourceShort: "Source",
      sourceWireFeed: "Source wire feed",
      targetDiameter: "Target wire Ø",
      targetShort: "Target",
      targetWireFeed: "Converted wire feed",
      sourceArea: "Source wire area",
      targetArea: "Target wire area",
      areaRatio: "Area ratio"
    },
    geometry: {
      aMass: "Throat thickness (a)",
      zMeasureInput: "Leg size (Z)",
      angle: "Fillet weld opening angle",
      angleShort: "Angle",
      targetSelection: "Calculation target",
      calculateZ: "Calculate Leg size (Z)",
      calculateZDescription: "Calculate leg size (Z) from throat thickness and angle.",
      calculateA: "Calculate throat (a)",
      calculateADescription: "Calculate throat thickness (a) from leg size and angle.",
      zMeasure: "Calculated leg size Z",
      calculatedAMass: "Calculated throat thickness",
      zMeasureInch: "Z size in inches",
      zMeasureFraction: "Z size as inch fraction",
      legFactor: "Leg factor",
      halfAngle: "Half angle"
    },
    sheetThickness: {
      aMass: "Throat thickness (a)",
      presetSelection: "Throat quick select",
      matrix: "Plate thickness table",
      recommendation: "Recommended plate thickness",
      froniusRecommendation: "Fronius recommendation",
      enCalculation: "EN 1993-1-8/NA:2020",
      julesCalculation: "Jules factor (0.83)",
      froniusMin: "Fronius min.",
      froniusMax: "Fronius max.",
      normMin: "Min. per EN 1993-1-8/NA",
      normMax: "Max. per EN 1993-1-8/NA",
      julesMin: "Min. with Jules factor",
      julesFactor: "Jules factor"
    },
    wireMaterial: {
      commonData: "Shared wire data",
      spoolRange: "Spool / range",
      rangeConsumption: "Range & consumption",
      spoolWeight: "Spool weight",
      spoolShort: "Spool",
      density: "Material density",
      densityShort: "Density",
      weldingTime: "Welding time",
      timeShort: "Time",
      material: "Filler material",
      customMaterial: "Custom value",
      wireLength: "Wire length",
      maxWeldTime: "Max. welding time",
      maxWeldTimeHours: "Max. welding time",
      depositionRate: "Deposition rate",
      wireConsumption: "Wire consumption",
      weightPerMeter: "Wire weight per meter",
      materials: {
        steel: "Steel",
        stainless: "Stainless",
        almg5: "AlMg5",
        alsi5: "AlSi5",
        inconel625: "Inconel",
        cusi3: "CuSi3"
      }
    },
    timeSpeed: {
      calculateSpeed: "Calculate travel speed",
      calculateDuration: "Calculate duration",
      weldLength: "Weld length",
      lengthShort: "Length",
      duration: "Duration",
      durationShort: "Duration",
      travelSpeedResult: "Calculated travel speed",
      durationResult: "Calculated duration",
      durationMinutes: "Duration in minutes",
      lengthPerSecond: "Weld length per second"
    },
    energy: {
      impulseMode: "From IE and length",
      powerMode: "From IP and speed",
      energyComparison: "Heat input comparison",
      impulseEnergy: "IE",
      weldLength: "Weld length",
      power: "IP",
      impulseMethod: "Heat input from IE",
      powerMethod: "Heat input from IP and travel speed",
      difference: "Difference",
      differencePercent: "Relative difference"
    },
    modes: {
      aMass: {
        label: "Calculate throat thickness",
        shortLabel: "Throat (a)",
        description: "Predicted fillet weld throat thickness with safety factor."
      },
      travelSpeed: {
        label: "Calculate travel speed",
        shortLabel: "Speed",
        description: "From throat thickness, wire diameter and wire feed speed."
      },
      wireFeed: {
        label: "Calculate wire feed speed",
        shortLabel: "Wire feed",
        description: "From throat thickness, wire diameter and travel speed."
      },
      wireDiameter: {
        label: "Calculate wire diameter",
        shortLabel: "Wire Ø",
        description: "From throat thickness, wire feed speed and travel speed."
      }
    },
    inputs: {
      aMass: "Throat thickness (a)",
      wireDiameter: "Wire Ø",
      wireFeed: "Wire feed speed",
      travelSpeed: "Travel speed"
    },
    labels: {
      predictedAMass: "Predicted throat thickness",
      rawAMass: "Theoretical throat before factor",
      travelSpeed: "Calculated travel speed",
      wireFeed: "Calculated wire feed",
      wireDiameter: "Calculated wire diameter",
      seamArea: "Throat area",
      wireArea: "Wire area",
      safetyFactor: "Safety factor",
      volumeRate: "Volume rate"
    }
  }
} satisfies Record<Language, object>;
